import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatDuration, formatNumber } from '../i18n.js';
import { useSites } from '../pages/Overview.js';
import { PulseStrip, hourState } from './SiteCharts.js';
import { Button, LinkButton, Notice } from './ui.js';

type Range = '24h' | '7d' | '30d' | '90d' | '365d';
type Incident = Schemas['Incident'];
const PERIODS: readonly Range[] = ['24h', '7d', '30d', '90d', '365d'];
const RANGE_MS: Record<Range, number> = {
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '90d': 90 * 86_400_000,
  '365d': 365 * 86_400_000,
};
/** Above this the home page counts as slow, unless the monitor says otherwise (checks/http). */
const SLOW_MS = 3000;

/** The clock, ticking every `ms` milliseconds. */
function useNow(ms: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** The site with its monitors; the home page monitor is the http one on the site address. */
function useHomeMonitor(siteId: string) {
  const site = useQuery({
    queryKey: ['site', siteId, 'detail'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}', { params: { path: { siteId } } })),
    refetchInterval: 30_000,
  });
  const home = site.data?.monitors.find((m) => m.type === 'http' && m.url === site.data.url);
  return { site: site.data, home };
}

function useUptime(siteId: string, range: Range, enabled = true) {
  return useQuery({
    queryKey: ['site', siteId, 'uptime', range],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } })),
    enabled,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
  });
}

/** Seconds as "0,56 s". */
const seconds = (ms: number, language: string) => `${formatNumber(ms / 1000, language, 2)} s`;

/**
 * State now, the next check and the last 24 hours (T-91, mock-up "Website: Erreichbarkeit").
 */
export function StatusTiles({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const now = useNow(1000);
  const { site, home } = useHomeMonitor(siteId);
  const hours = useSites().data?.items.find((s) => s.id === siteId)?.uptimeHours ?? [];
  if (!site || !home) return null;
  const paused = site.status === 'paused';
  const state = paused ? 'paused' : home.state;
  const since = home.stateChangedAt ? now - Date.parse(home.stateChangedAt) : null;
  const left = Math.max(0, Date.parse(home.nextRunAt) - now);
  const share = Math.min(1, left / (home.intervalSeconds * 1000));
  const slowHours = hours.filter((h) => hourState(h) === 'degraded').length;
  const downHours = hours.filter((h) => h.down).length;
  const tone =
    state === 'up' ? 'text-green-800' : state === 'down' ? 'text-red-800' : 'text-slate-900';
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <section className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-normal text-slate-700">{t('availability.now')}</h2>
        <p className={`text-2xl font-extrabold ${tone}`}>{t(`status.${state}`)}</p>
        {since !== null && !paused ? (
          <p className="text-sm text-slate-800">
            {t(state === 'up' ? 'availability.upSince' : 'availability.since', {
              duration: formatDuration(since / 1000, i18n.language),
            })}
          </p>
        ) : null}
      </section>
      <section className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <svg viewBox="0 0 36 36" className="size-14 shrink-0" aria-hidden="true">
          <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
          <circle
            cx="18"
            cy="18"
            r="15.9155"
            fill="none"
            stroke="#1d4ed8"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={`${share * 100} ${100 - share * 100}`}
            transform="rotate(-90 18 18)"
          />
        </svg>
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-normal text-slate-700">{t('availability.next')}</h2>
          <p className="text-lg font-bold tabular-nums">
            {paused
              ? t('status.paused')
              : left === 0
                ? t('availability.running')
                : t('availability.inSeconds', { count: Math.ceil(left / 1000) })}
          </p>
          <p className="text-sm text-slate-800">
            {t('availability.every', {
              interval: formatDuration(home.intervalSeconds, i18n.language),
            })}
          </p>
        </div>
      </section>
      <section className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-normal text-slate-700">{t('availability.last24h')}</h2>
        {hours.length > 0 ? <PulseStrip hours={hours} /> : null}
        <p className="text-sm text-slate-800">
          {t('availability.outageHours', { count: downHours })} ·{' '}
          {t('availability.slowHours', { count: slowHours })}
        </p>
      </section>
    </div>
  );
}

/** Outages within the window: how many and how long, ongoing ones up to now. */
function outagesIn(incidents: readonly Incident[], since: number, now: number) {
  const hits = incidents.filter(
    (i) => i.kind === 'downtime' && (i.resolvedAt ? Date.parse(i.resolvedAt) : now) >= since,
  );
  const total = hits.reduce(
    (n, i) =>
      n +
      Math.max(
        0,
        (i.resolvedAt ? Date.parse(i.resolvedAt) : now) - Math.max(since, Date.parse(i.startedAt)),
      ) /
        1000,
    0,
  );
  return { count: hits.length, total };
}

/** Uptime per period with its outages; periods beyond the plan point to the plans (T-91). */
export function PeriodStrip({
  siteId,
  incidents,
}: {
  siteId: string;
  incidents: readonly Incident[];
}) {
  const { t, i18n } = useTranslation();
  const first = useUptime(siteId, '24h');
  const allowed = first.data?.allowedRanges ?? [];
  // One daily series for the longest period of the plan; the shorter ones are its last days.
  const longest = [...PERIODS].reverse().find((r) => r !== '24h' && allowed.includes(r));
  const daily = useUptime(siteId, longest ?? '7d', longest !== undefined);
  const pctOf = (range: Range): number | null => {
    if (range === '24h') return first.data?.uptimePct ?? null;
    if (!daily.data) return null;
    const since = Date.now() - RANGE_MS[range];
    const days = daily.data.series.filter((p) => Date.parse(p.t) >= since - 86_400_000 / 2);
    const checks = days.reduce((n, p) => n + p.checks, 0);
    const failed = days.reduce((n, p) => n + p.failed, 0);
    return checks > 0 ? Math.round(((checks - failed) / checks) * 10000) / 100 : null;
  };
  if (!first.data) return null;
  const now = Date.now();
  return (
    <section
      aria-label={t('availability.periods')}
      className="grid grid-cols-2 overflow-hidden rounded-xl border border-slate-200 bg-white lg:grid-cols-5"
    >
      {PERIODS.map((range, i) => {
        const label = t(`availability.range.${range}`);
        if (!allowed.includes(range))
          return (
            <div
              key={range}
              className="flex flex-col gap-1 border-t border-l border-slate-100 bg-slate-50 p-4 lg:border-t-0"
            >
              <span className="text-sm text-slate-700">{label}</span>
              <span className="font-semibold text-slate-800">{t('availability.locked')}</span>
              <Link to="/billing" className="text-sm font-semibold text-blue-700 hover:underline">
                {t('availability.plans')}
              </Link>
            </div>
          );
        const pct = pctOf(range);
        const out = outagesIn(incidents, now - RANGE_MS[range], now);
        const good = pct !== null && pct >= 99.9;
        return (
          <div
            key={range}
            className={`flex flex-col gap-1.5 border-slate-100 p-4 ${i > 0 ? 'border-l' : ''} ${i > 1 ? 'border-t lg:border-t-0' : ''}`}
          >
            <span className="text-sm text-slate-700">{label}</span>
            <strong className={`text-2xl tabular-nums ${good ? 'text-green-800' : ''}`}>
              {pct === null ? '–' : `${formatNumber(pct, i18n.language, 2)} %`}
            </strong>
            <span aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-slate-200">
              <span
                className={`block h-full ${good ? 'bg-green-600' : pct !== null && pct >= 99 ? 'bg-amber-400' : 'bg-red-600'}`}
                style={{ width: `${pct === null ? 0 : Math.max(4, ((pct - 95) / 5) * 100)}%` }}
              />
            </span>
            <span className="text-sm text-slate-800">
              {out.count === 0
                ? t('availability.noOutages')
                : t('availability.outages', {
                    count: out.count,
                    duration: formatDuration(out.total, i18n.language),
                  })}
            </span>
          </div>
        );
      })}
    </section>
  );
}

/** Response time of the home page with the slow line, plus fastest, average and slowest (T-91). */
export function ResponseChart({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<'24h' | '7d'>('24h');
  const { home } = useHomeMonitor(siteId);
  const uptime = useUptime(siteId, range);
  const config = home?.config as { degraded_threshold_ms?: unknown } | undefined;
  const slow =
    typeof config?.degraded_threshold_ms === 'number' ? config.degraded_threshold_ms : SLOW_MS;
  const points = (uptime.data?.series ?? []).filter(
    (p): p is typeof p & { avgResponseMs: number } => p.avgResponseMs !== null,
  );
  const values = points.map((p) => p.avgResponseMs);
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const w = 800;
  const h = 200;
  const top = Math.max(slow * 1.2, ...values.map((v) => v * 1.1));
  const y = (v: number) => 16 + (1 - v / top) * (h - 40);
  const x = (n: number) => 56 + (n * (w - 72)) / Math.max(1, points.length - 1);
  const time = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      ...(range === '7d' ? { day: '2-digit', month: '2-digit' } : {}),
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  const ticks = [0, slow / 2, slow].filter((v) => v <= top);
  const line = points.map((p, n) => `${x(n)},${y(p.avgResponseMs)}`).join(' ');
  const figures: [string, number | null][] = [
    [t('availability.avg'), avg],
    [t('availability.fastest'), values.length ? Math.min(...values) : null],
    [t('availability.slowest'), values.length ? Math.max(...values) : null],
  ];
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('availability.response')}</h2>
        <div
          role="group"
          aria-label={t('availability.rangeLabel')}
          className="inline-flex overflow-hidden rounded-lg border border-slate-300"
        >
          {(['24h', '7d'] as const).map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={range === r}
              onClick={() => setRange(r)}
              className={`min-h-10 px-3 text-sm font-semibold ${range === r ? 'bg-[#0b1b3f] text-white' : 'bg-white text-slate-900 hover:bg-blue-50'}`}
            >
              {t(`availability.range.${r}`)}
            </button>
          ))}
        </div>
      </div>
      {points.length < 2 ? (
        <p className="py-8 text-center text-slate-700">{t('site.chart.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${w} ${h}`}
            className="h-auto w-full min-w-[34rem]"
            role="img"
            aria-label={t('availability.chartSr', {
              avg: seconds(avg ?? 0, i18n.language),
              max: seconds(Math.max(...values), i18n.language),
            })}
          >
            {ticks.map((v) => (
              <g key={v}>
                <line x1="56" x2={w - 16} y1={y(v)} y2={y(v)} stroke="#e2e8f0" />
                <text x="48" y={y(v) + 4} textAnchor="end" fontSize="12" fill="#475569">
                  {seconds(v, i18n.language)}
                </text>
              </g>
            ))}
            <line
              x1="56"
              x2={w - 16}
              y1={y(slow)}
              y2={y(slow)}
              stroke="#b45309"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
            <text x={w - 18} y={y(slow) - 6} textAnchor="end" fontSize="12" fill="#92400e">
              {t('availability.slowLine', { value: seconds(slow, i18n.language) })}
            </text>
            <path
              d={`M${x(0)},${y(0)} L${line.replaceAll(' ', ' L')} L${x(points.length - 1)},${y(0)} Z`}
              fill="#dbeafe"
            />
            <polyline
              points={line}
              fill="none"
              stroke="#1d4ed8"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <text x="56" y={h - 4} fontSize="12" fill="#475569">
              {time(points[0]?.t ?? '')}
            </text>
            <text x={w - 16} y={h - 4} textAnchor="end" fontSize="12" fill="#475569">
              {time(points[points.length - 1]?.t ?? '')}
            </text>
          </svg>
        </div>
      )}
      <dl className="grid grid-cols-3 gap-2">
        {figures.map(([label, v]) => (
          <div key={label} className="rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-sm text-slate-700">{label}</dt>
            <dd className="text-xl font-extrabold tabular-nums">
              {v === null ? '–' : seconds(v, i18n.language)}
            </dd>
          </div>
        ))}
      </dl>
      {avg !== null ? (
        <p className="text-sm text-slate-800">
          {t(
            avg < 1000 ? 'availability.fast' : avg < slow ? 'availability.ok' : 'availability.slow',
            {
              value: seconds(slow, i18n.language),
            },
          )}
        </p>
      ) : null}
    </section>
  );
}

/** Hetzner sites of the probe as town names; other region codes stay unnamed. */
const TOWNS: Record<string, string> = {
  fsn1: 'Falkenstein',
  nbg1: 'Nürnberg',
  hel1: 'Helsinki',
};
const townOf = (region: string | null) =>
  Object.entries(TOWNS).find(([code]) => region?.includes(code))?.[1] ?? null;

/** Line styles per location: colour and dash, so the two differ without colour too. */
const LOCATION_STYLE = {
  primary: { stroke: '#1d4ed8', dash: undefined },
  secondary: { stroke: '#0f766e', dash: '7 5' },
} as const;

/**
 * Response time of the home page from both check locations (T-93). Shown only while the second
 * location runs and has samples; one line alone would repeat the chart above.
 */
export function ResponseByLocation({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<'24h' | '7d'>('24h');
  // The same query as the card of the second location: no extra request when it is not running.
  const locations = useQuery({
    queryKey: ['site', siteId, 'locations'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/locations', { params: { path: { siteId } } })),
  });
  const running = !!locations.data?.available && !!locations.data.active;
  const data = useQuery({
    queryKey: ['site', siteId, 'response-locations', range],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/response-locations', {
          params: { path: { siteId }, query: { range } },
        }),
      ),
    enabled: running,
  });
  const lines = data.data?.locations ?? [];
  const primary = lines.find((l) => l.key === 'primary');
  const secondary = lines.find((l) => l.key === 'secondary');
  if (!running || !secondary) return null;

  const times = lines.flatMap((l) => l.series.map((p) => Date.parse(p.t)));
  const from = Math.min(...times);
  const to = Math.max(...times);
  const top = Math.max(1000, ...lines.flatMap((l) => l.series.map((p) => p.avgResponseMs * 1.15)));
  const w = 800;
  const h = 200;
  const x = (iso: string) => 56 + ((Date.parse(iso) - from) * (w - 72)) / Math.max(1, to - from);
  const y = (v: number) => 16 + (1 - v / top) * (h - 40);
  const ticks = [0, top / 2, top].map((v) => Math.round(v / 100) * 100);
  const time = (ms: number) =>
    new Intl.DateTimeFormat(i18n.language, {
      ...(range === '7d' ? { day: '2-digit', month: '2-digit' } : {}),
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ms));
  const town = townOf(secondary.region);
  const name = (key: 'primary' | 'secondary') =>
    key === 'primary'
      ? t('availability.byLocation.primary')
      : town
        ? t('availability.byLocation.secondaryIn', { town })
        : t('availability.byLocation.secondary');
  const a = primary?.avgResponseMs ?? null;
  const b = secondary.avgResponseMs;
  const verdict =
    a === null || b === null
      ? null
      : b > a * 1.5 && b - a > 300
        ? 'slowerThere'
        : a > b * 1.5 && a - b > 300
          ? 'fasterThere'
          : 'similar';

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('availability.byLocation.title')}</h2>
          <p className="max-w-prose text-sm text-slate-700">{t('availability.byLocation.intro')}</p>
        </div>
        <div
          role="group"
          aria-label={t('availability.rangeLabel')}
          className="inline-flex overflow-hidden rounded-lg border border-slate-300"
        >
          {(['24h', '7d'] as const).map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={range === r}
              onClick={() => setRange(r)}
              className={`min-h-10 px-3 text-sm font-semibold ${range === r ? 'bg-[#0b1b3f] text-white' : 'bg-white text-slate-900 hover:bg-blue-50'}`}
            >
              {t(`availability.range.${r}`)}
            </button>
          ))}
        </div>
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {lines.map((l) => (
          <li key={l.key} className="flex items-center gap-2 text-sm">
            <svg width="28" height="8" aria-hidden="true">
              <line
                x1="0"
                x2="28"
                y1="4"
                y2="4"
                stroke={LOCATION_STYLE[l.key].stroke}
                strokeWidth="3"
                strokeDasharray={LOCATION_STYLE[l.key].dash}
              />
            </svg>
            <span className="text-slate-800">{name(l.key)}</span>
            <strong className="tabular-nums">
              {l.avgResponseMs === null
                ? '–'
                : t('availability.byLocation.avg', {
                    value: seconds(l.avgResponseMs, i18n.language),
                  })}
            </strong>
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="h-auto w-full min-w-[34rem]"
          role="img"
          aria-label={t('availability.byLocation.chartSr', {
            primary: a === null ? '–' : seconds(a, i18n.language),
            secondary: b === null ? '–' : seconds(b, i18n.language),
          })}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line x1="56" x2={w - 16} y1={y(v)} y2={y(v)} stroke="#e2e8f0" />
              <text x="48" y={y(v) + 4} textAnchor="end" fontSize="12" fill="#475569">
                {seconds(v, i18n.language)}
              </text>
            </g>
          ))}
          {lines.map((l) => (
            <polyline
              key={l.key}
              points={l.series.map((p) => `${x(p.t)},${y(p.avgResponseMs)}`).join(' ')}
              fill="none"
              stroke={LOCATION_STYLE[l.key].stroke}
              strokeWidth="2"
              strokeDasharray={LOCATION_STYLE[l.key].dash}
              strokeLinejoin="round"
            />
          ))}
          <text x="56" y={h - 4} fontSize="12" fill="#475569">
            {time(from)}
          </text>
          <text x={w - 16} y={h - 4} textAnchor="end" fontSize="12" fill="#475569">
            {time(to)}
          </text>
        </svg>
      </div>
      {verdict ? (
        <p className="text-sm text-slate-800">{t(`availability.byLocation.${verdict}`)}</p>
      ) : null}
    </section>
  );
}

/**
 * "Testnachricht" and "Pausieren" on the availability page (T-98): a test to every confirmed
 * recipient of outage alerts, and pausing or resuming the checks of this site.
 */
export function AvailabilityActions({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { site } = useHomeMonitor(siteId);
  const paused = site?.status === 'paused';
  // The recipients are read on click only: the page itself needs no extra request.
  const test = useMutation({
    mutationFn: async () => {
      const { items } = await unwrap(api.GET('/alert-channels'));
      const targets = items.filter(
        (c) => c.verified && c.enabled && c.receives.includes('critical'),
      );
      const results = await Promise.allSettled(
        targets.map((c) =>
          unwrap(
            api.POST('/alert-channels/{channelId}/test', { params: { path: { channelId: c.id } } }),
          ),
        ),
      );
      const sent = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (sent === 0 && failed) throw failed.reason;
      return { sent, none: targets.length === 0 };
    },
  });
  const pause = useMutation({
    mutationFn: (status: 'active' | 'paused') =>
      unwrap(api.PATCH('/sites/{siteId}', { params: { path: { siteId } }, body: { status } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
  return (
    <div className="flex flex-col items-end gap-2">
      <span className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" busy={test.isPending} onClick={() => test.mutate()}>
          {t('availability.actions.test')}
        </Button>
        {site ? (
          <Button
            variant="secondary"
            busy={pause.isPending}
            onClick={() => pause.mutate(paused ? 'active' : 'paused')}
          >
            {t(paused ? 'availability.actions.resume' : 'availability.actions.pause')}
          </Button>
        ) : null}
        <LinkButton to="/notifications" variant="secondary">
          {t('availability.notifications')}
        </LinkButton>
        <LinkButton to={`/sites/${siteId}/settings`} variant="secondary">
          {t('siteNav.settings')}
        </LinkButton>
      </span>
      <div role="status" className="flex max-w-md flex-col gap-2 empty:hidden">
        {test.data ? (
          test.data.none ? (
            <Notice kind="warning">
              {t('availability.actions.noRecipients')}{' '}
              <Link to="/notifications" className="font-semibold underline">
                {t('availability.notifications')}
              </Link>
            </Notice>
          ) : (
            <Notice kind="success">
              {t('availability.actions.sent', { count: test.data.sent })}
            </Notice>
          )
        ) : null}
        {test.isError ? <Notice kind="error">{errorMessage(test.error, t)}</Notice> : null}
        {pause.isError ? <Notice kind="error">{errorMessage(pause.error, t)}</Notice> : null}
        {paused ? <Notice kind="info">{t('availability.actions.paused')}</Notice> : null}
      </div>
    </div>
  );
}
